// BODi product card gallery: slider controls and Sezerium variant image swaps
(function () {
  const SELECTORS = {
    gallery: '._bodi-product-card-gallery',
    // Each variant renders its own track and dot group; all but the current one carry [hidden].
    track: '._bodi-product-gallery--inner:not([hidden])',
    // Only keyed tracks take part in variant swaps. A product with no curated variant
    // media renders a single unkeyed track, which must never be hidden.
    anyTrack: '._bodi-product-gallery--inner[data-variant-id]',
    slide: '._bodi-product-card--media_item',
    button: '._bodi-product-gallery-navigation__button',
    previousButton: '._bodi-product-gallery-navigation__button--prev',
    nextButton: '._bodi-product-gallery-navigation__button--next',
    dot: '._bodi-product-gallery-navigation__dots:not([hidden]) ._bodi-product-gallery-navigation__dot',
    dots: '._bodi-product-gallery-navigation__dots[data-variant-id]',
  };

  const ACTIVE_DOT_CLASS = '_bodi-product-gallery-navigation__dot--active';
  const SCROLL_EDGE_TOLERANCE = 1;
  const sliderCache = new WeakMap();

  function createSlider(gallery) {
    const track = gallery.querySelector(SELECTORS.track);
    const previousButton = gallery.querySelector(SELECTORS.previousButton);
    const nextButton = gallery.querySelector(SELECTORS.nextButton);

    if (!track || !previousButton || !nextButton) return null;

    return {
      track,
      previousButton,
      nextButton,
      dots: Array.from(gallery.querySelectorAll(SELECTORS.dot)),
      updateFrame: null,
    };
  }

  function getSlider(gallery) {
    const cachedSlider = sliderCache.get(gallery);
    const cacheIsCurrent =
      cachedSlider &&
      gallery.contains(cachedSlider.track) &&
      // A track that was just hidden by a variant swap is still contained, so check it too.
      !cachedSlider.track.hasAttribute('hidden') &&
      gallery.contains(cachedSlider.previousButton) &&
      gallery.contains(cachedSlider.nextButton);

    if (cacheIsCurrent) return cachedSlider;
    sliderCache.delete(gallery);

    const slider = createSlider(gallery);
    if (slider) sliderCache.set(gallery, slider);

    return slider;
  }

  function getScrollAmount(track) {
    const slide = track.querySelector(SELECTORS.slide);
    if (!slide) return track.clientWidth;

    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    return slide.offsetWidth + gap;
  }

  function updateButtons(slider) {
    const { track, previousButton, nextButton } = slider;
    const maxScroll = track.scrollWidth - track.clientWidth;
    const hasOverflow = maxScroll > SCROLL_EDGE_TOLERANCE;

    previousButton.disabled = !hasOverflow || track.scrollLeft <= SCROLL_EDGE_TOLERANCE;
    nextButton.disabled = !hasOverflow || track.scrollLeft >= maxScroll - SCROLL_EDGE_TOLERANCE;
  }

  function updateDots(slider) {
    const { track, dots } = slider;
    if (!dots.length) return;

    const activeIndex = Math.round(track.scrollLeft / getScrollAmount(track));

    dots.forEach((dot, index) => {
      dot.classList.toggle(ACTIVE_DOT_CLASS, index === activeIndex);
    });
  }

  function updateSlider(slider) {
    updateButtons(slider);
    updateDots(slider);
  }

  function scheduleUpdate(slider) {
    if (slider.updateFrame !== null) return;

    slider.updateFrame = requestAnimationFrame(() => {
      slider.updateFrame = null;
      updateSlider(slider);
    });
  }

  function scrollSliderBy(slider, direction) {
    slider.track.scrollBy({
      left: direction * getScrollAmount(slider.track),
      behavior: 'smooth',
    });
  }

  function scrollSliderTo(slider, index) {
    slider.track.scrollTo({
      left: index * getScrollAmount(slider.track),
      behavior: 'smooth',
    });
  }

  function onClick(event) {
    if (!(event.target instanceof Element)) return;

    const button = event.target.closest(SELECTORS.button);
    const dot = event.target.closest(SELECTORS.dot);
    const target = button || dot;
    const gallery = target?.closest(SELECTORS.gallery);
    if (!target || !gallery) return;

    event.preventDefault();
    event.stopPropagation();

    const slider = getSlider(gallery);
    if (!slider) return;

    if (button?.matches(SELECTORS.previousButton)) scrollSliderBy(slider, -1);
    if (button?.matches(SELECTORS.nextButton)) scrollSliderBy(slider, 1);
    if (dot) scrollSliderTo(slider, slider.dots.indexOf(dot));
  }

  function onScroll(event) {
    const track = event.target;
    if (!(track instanceof Element) || !track.matches(SELECTORS.track)) return;

    const gallery = track.closest(SELECTORS.gallery);
    const slider = gallery && getSlider(gallery);
    if (slider) scheduleUpdate(slider);
  }

  function initializeGallery(gallery) {
    const slider = getSlider(gallery);
    if (slider) updateSlider(slider);
  }

  function initializeGalleries(root = document) {
    if (root instanceof Element && root.matches(SELECTORS.gallery)) {
      initializeGallery(root);
    }

    root.querySelectorAll?.(SELECTORS.gallery).forEach(initializeGallery);
  }

  document.addEventListener('click', onClick, true);
  document.addEventListener('scroll', onScroll, { capture: true, passive: true });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeGalleries(), { once: true });
  } else {
    initializeGalleries();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initializeGalleries(event.target);
  });

  /*
   * Sezerium ("Variants on Collection") renders the variant cards on collection, search and
   * featured-collection grids. It owns which variant a card is showing, so it is the only
   * thing that can tell us which of the pre-rendered tracks to reveal.
   *
   * The variant images live in the custom.merch_additional_variant_images variant metafield,
   * which cannot come through the app's single variant.metafield slot (custom.badge uses it).
   * So the block renders every variant's track server-side and we only toggle visibility.
   */

  let hasWarnedAboutVariantId = false;

  function readVariantId(variant) {
    if (!variant) return null;

    const id = variant.id ?? variant.variantId ?? variant.variant_id;
    if (id != null) return String(id);

    if (!hasWarnedAboutVariantId) {
      hasWarnedAboutVariantId = true;
      console.warn('[bodi] Could not read a variant id from the Sezerium payload:', variant);
    }

    return null;
  }

  function showVariant(gallery, variantId) {
    const tracks = gallery.querySelectorAll(SELECTORS.anyTrack);
    if (!tracks.length) return;

    const hasMatch = Array.from(tracks).some((track) => track.dataset.variantId === variantId);
    // Variants with no media of their own are not rendered; fall back to the seed track.
    const targetId = hasMatch ? variantId : gallery.dataset.seedVariantId;

    let changed = false;

    const toggle = (element) => {
      const shouldShow = element.dataset.variantId === targetId;
      if (element.hasAttribute('hidden') === !shouldShow) return;

      element.toggleAttribute('hidden', !shouldShow);
      changed = true;
    };

    tracks.forEach(toggle);
    gallery.querySelectorAll(SELECTORS.dots).forEach(toggle);

    if (!changed) return;

    gallery.dataset.variantId = targetId;

    const visibleTrack = gallery.querySelector(SELECTORS.track);
    if (visibleTrack) visibleTrack.scrollLeft = 0;

    initializeGallery(gallery);
  }

  document.addEventListener('sezerium:image:update', (event) => {
    const { el, variant } = event.detail || {};
    if (!el) return;

    const variantId = readVariantId(variant);
    if (!variantId) return;

    const galleries =
      el.matches?.(SELECTORS.gallery) ? [el] : Array.from(el.querySelectorAll(SELECTORS.gallery));

    galleries.forEach((gallery) => showVariant(gallery, variantId));
  });

  /*
   * Sezerium rebuilds the grid without firing DOMContentLoaded or shopify:section:load, so
   * without these the arrow disabled-state and active dot go stale against the new DOM.
   */
  document.addEventListener('sezerium:js:unload', () => initializeGalleries());
  document.addEventListener('sezerium:infinite:load', () => initializeGalleries());
})();
